import { CalendarIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import {
    LuBaby,
    LuChartColumn,
    LuClipboardList,
    LuClock,
    LuGift,
    LuHandshake,
    LuInfo,
    LuLandmark,
    LuMailQuestion,
    LuMessageCircle,
    LuSparkles,
    LuTimer,
    LuUserPlus,
    LuX,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { InsightPayloadUnion, InsightPrimaryAction, InsightTag, InsightView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { titleCase } from '@/utils';
import { useDockState } from '@/views/dock/dock-state';
import { TextLink } from '@/widgets';
import { getHouseholdName, splitName, useHouseholdRoutePaths } from 'utils';
import { useAiChatSidebarState } from 'views/ai-chat/ai-chat-state';
import { useAiChatNewConversation } from 'views/ai-chat/useAiChatNewConversation';
import { useAllHouseholdMap } from 'views/household/household-queries';
import { HouseholdDrawer } from 'views/household/HouseholdDrawer';

import { buildInsightPrompt } from './insight-prompts';
import { useIgnoreInsight, useUnignoreInsight } from './insight-queries';
import { getInsightSubtitle } from './insight-utils';
import { WeekInReviewSummary } from './WeekInReviewSummary';

interface InsightItemProps {
    insight: InsightView;
    onSchedule: (defaultHousehold?: { value: string; label: string }) => void;
}

export const InsightItem = ({ insight, onSchedule }: InsightItemProps) => {
    const { open: openChat } = useAiChatSidebarState();
    const { startNewConversation } = useAiChatNewConversation();
    const { addCreateOpportunityDockItem } = useDockState();
    const householdMap = useAllHouseholdMap();
    const navigate = useNavigate();
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    const { mutate: ignoreInsight } = useIgnoreInsight();
    const { mutate: unignoreInsight } = useUnignoreInsight();
    const [prospectDraft, setProspectDraft] = useState<{ firstName: string; lastName: string } | null>(null);
    const [showSummary, setShowSummary] = useState(false);

    const handlePrimaryAction = () => {
        const { payload } = insight;
        if (insight.primaryAction === InsightPrimaryAction.VIEW_SUMMARY && payload.type === InsightTag.R1) {
            setShowSummary(true);
            return;
        }

        if (insight.primaryAction === InsightPrimaryAction.CREATE_OPPORTUNITY && payload.type === InsightTag.N3) {
            addCreateOpportunityDockItem(payload.householdGUID, {
                name: payload.suggestedOpportunityName || payload.assetType || '',
                description: payload.suggestedNextStep || payload.evidence || '',
                value: payload.approximateValue || '',
            });
            return;
        }

        if (insight.primaryAction === InsightPrimaryAction.ADD_AS_PROSPECT && payload.type === InsightTag.N5) {
            setProspectDraft(splitName(payload.prospectName));
            return;
        }

        const prompt = buildInsightPrompt(insight);
        if (prompt) {
            openChat('sidekick');
            startNewConversation(prompt);
        }
    };

    const handleSchedule = () => {
        const { payload } = insight;
        if (isHouseholdPayload(payload)) {
            const household = householdMap.get(payload.householdGUID);
            onSchedule({ value: payload.householdGUID, label: getHouseholdName(household) });
        } else {
            onSchedule();
        }
    };

    const handleIgnore = () => {
        ignoreInsight(insight.insightGUID);
        toast('Insight ignored', {
            action: { label: 'Undo', onClick: () => unignoreInsight(insight.insightGUID) },
        });
    };

    return (
        <div className="border-border hover:bg-cocoa-0/50 flex items-center gap-6 border-t px-1.5 py-4 last:border-b">
            <InsightIcon tag={insight.tag} />

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <h4 className="text-cocoa-1000 text-base font-thin">{insight.title}</h4>
                    <InsightReason insight={insight} />
                </div>
                <InsightSubtitle insight={insight} />
            </div>

            <div className="flex shrink-0 items-center gap-2 pr-2">
                <Button
                    variant="outline2"
                    size="sm"
                    onClick={handlePrimaryAction}
                    className="gap-1.5 rounded-full px-4"
                >
                    <LuSparkles className="h-4 w-4" />
                    {titleCase(insight.primaryAction)}
                </Button>
                {insight.primaryAction !== InsightPrimaryAction.VIEW_SUMMARY && (
                    <Button
                        variant="outline2"
                        size="icon"
                        onClick={handleSchedule}
                        className="h-10 w-10 rounded-full border-gray-300"
                    >
                        <CalendarIcon className="h-4 w-4" />
                    </Button>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost2"
                            size="icon"
                            aria-label="Ignore insight"
                            onClick={handleIgnore}
                            className="h-10 w-10 rounded-full border-gray-300"
                        >
                            <LuX className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Ignore</TooltipContent>
                </Tooltip>
            </div>

            {insight.payload.type === InsightTag.R1 && (
                <WeekInReviewSummary payload={insight.payload} open={showSummary} onOpenChange={setShowSummary} />
            )}

            {prospectDraft && (
                <HouseholdDrawer
                    isProspectRoute
                    defaultPerson={prospectDraft}
                    onClose={() => setProspectDraft(null)}
                    onSuccess={(globalAccountGUID) => {
                        setProspectDraft(null);
                        navigate(HouseholdIndividualPageRoutePath(globalAccountGUID));
                    }}
                />
            )}
        </div>
    );
};

const isHouseholdPayload = (
    payload: InsightPayloadUnion,
): payload is Extract<InsightPayloadUnion, { householdGUID: string }> =>
    payload.type === InsightTag.E3 ||
    payload.type === InsightTag.E4 ||
    payload.type === InsightTag.N1 ||
    payload.type === InsightTag.N2 ||
    payload.type === InsightTag.N3 ||
    payload.type === InsightTag.N4 ||
    payload.type === InsightTag.N5 ||
    payload.type === InsightTag.N6 ||
    payload.type === InsightTag.R2 ||
    payload.type === InsightTag.R3;

// An on-demand "why this surfaced" explanation. The reason is model-generated (the R2/R3 reply classifier's
// justification) and only present on those payloads, so the icon renders only when a reason is available.
// Hover shows a tooltip; clicking pins the same text in a popover (the only affordance on touch devices).
// Radix dismisses the tooltip on trigger click, so the two never show together.
const InsightReason = ({ insight }: { insight: InsightView }) => {
    const reason = 'reason' in insight.payload ? insight.payload.reason?.trim() : undefined;
    if (!reason) {
        return null;
    }
    return (
        <Popover>
            <Tooltip>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            aria-label="Why this was surfaced"
                            className="text-cocoa-400 hover:text-cocoa-700 shrink-0"
                        >
                            <LuInfo className="h-4 w-4" />
                        </button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm font-normal">{reason}</TooltipContent>
            </Tooltip>
            <PopoverContent side="top" className="w-auto max-w-xs text-sm font-normal">
                {reason}
            </PopoverContent>
        </Popover>
    );
};

const InsightSubtitle = ({ insight }: { insight: InsightView }) => {
    const { payload } = insight;
    if ('subjectPersonOrgGUID' in payload || 'householdGUID' in payload) {
        const { prefix, suffix, to } = getInsightSubtitle(insight);
        return (
            <p className="text-cocoa-500 text-sm">
                <TextLink to={to}>{prefix}</TextLink> — {suffix}
            </p>
        );
    }

    return <p className="text-cocoa-500 text-sm">{insight.subtitle?.plainText ?? ''}</p>;
};

const getIcon = (tag: InsightTag) => {
    switch (tag) {
        case InsightTag.E1:
            return <LuGift className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.E2:
            return <CalendarIcon className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.E3:
            return <LuClock className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.E4:
            return <LuBaby className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.N1:
            return <LuTimer className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.N2:
            return <LuSparkles className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.N3:
            return <LuLandmark className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.N4:
            return <LuHandshake className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.N5:
            return <LuUserPlus className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.N6:
            return <LuChartColumn className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.R1:
            return <LuClipboardList className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.R2:
            return <LuMessageCircle className="text-cocoa-700 h-6 w-6" />;
        case InsightTag.R3:
            return <LuMailQuestion className="text-cocoa-700 h-6 w-6" />;
        default:
            return <LuSparkles className="text-cocoa-700 h-6 w-6" />;
    }
};

const InsightIcon = ({ tag }: { tag: InsightTag }) => (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#F8F7F5] p-2">
        {getIcon(tag)}
    </div>
);
